import React, { useEffect, useState, useRef } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ShoppingBag, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import Seo from "../components/Seo";
import { Breadcrumbs } from "../components/Breadcrumbs";
import { ShippingNotice } from "../components/ShippingNotice";
import { api, money, sized, srcSet } from "../lib/api";
import { useCart } from "../lib/cart";
import { gaViewItem } from "../lib/analytics";

const Spec = ({ label, value }) => value ? (
  <div className="flex justify-between py-3 border-b border-[#E4E1DB] text-sm">
    <span className="text-[#4A4947]">{label}</span>
    <span className="text-right">{value}</span>
  </div>
) : null;

export default function ArtworkDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { addItem } = useCart();
  const [p, setP] = useState(null);
  const [related, setRelated] = useState([]);
  const [activeImg, setActiveImg] = useState(0);
  const [notFound, setNotFound] = useState(false);
  const [qty, setQty] = useState(1);
  const [wlEmail, setWlEmail] = useState("");
  const [wlDone, setWlDone] = useState(false);
  const [wlBusy, setWlBusy] = useState(false);
  const viewedRef = useRef(null);

  useEffect(() => {
    setP(null); setNotFound(false); setActiveImg(0); setQty(1);
    api.get(`/products/${slug}`).then((r) => {
      setP(r.data);
      if (viewedRef.current !== slug) { viewedRef.current = slug; gaViewItem(r.data); }
      if (r.data.related_story_slugs?.length) {
        Promise.all(r.data.related_story_slugs.map((s) => api.get(`/stories/${s}`).then((x) => x.data).catch(() => null)))
          .then((arr) => setRelated(arr.filter(Boolean)));
      } else setRelated([]);
    }).catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) return <div className="max-w-3xl mx-auto px-5 py-32 text-center"><h1 className="font-display text-4xl">Artwork not found</h1><Link to="/etchings" className="link-underline mt-4 inline-block">Back to etchings</Link></div>;
  if (!p) return <div className="max-w-7xl mx-auto px-5 py-32" data-testid="artwork-loading"><div className="animate-pulse h-96 bg-[#F0EFEA]" /></div>;

  const images = [p.primary_image, ...(p.additional_images || [])].filter(Boolean);
  const inv = Number(p.inventory ?? 0);
  const sold = p.availability === "sold" || inv <= 0;
  const reserved = p.availability === "reserved";
  const unavailable = sold || reserved;

  const add = () => {
    const n = Math.min(inv, Math.max(1, qty));
    addItem({ kind: "etching", product_slug: p.slug, title: name, price: p.price, image: p.primary_image, quantity: n, inventory: inv });
    toast.success(`${name} added to cart${n > 1 ? ` (×${n})` : ""}`);
  };

  const joinWaitlist = async (e) => {
    e.preventDefault();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(wlEmail)) { toast.error("Please enter a valid email address."); return; }
    setWlBusy(true);
    try {
      await api.post("/waitlist", { email: wlEmail, product_slug: p.slug });
      setWlDone(true);
      toast.success("You're on the list — we'll be in touch.");
    } catch { toast.error("Something went wrong. Please try again."); } finally { setWlBusy(false); }
  };

  const name = p.display_title || p.title;
  // EU/EEA — the statutory right-of-withdrawal basis confirmed by the studio owner.
  const EU_EEA = ["AT","BE","BG","HR","CY","CZ","DK","EE","FI","FR","DE","GR","HU","IE","IT","LV","LT","LU","MT","NL","PL","PT","RO","SK","SI","ES","SE","IS","LI","NO"];
  // 14-day right of withdrawal for non-personalised originals (these product pages are originals).
  // Buyer pays return postage; returns by mail to the Utrecht address. Custom/personalised etchings
  // are excluded from this policy and carry no Product/Offer markup.
  const returnPolicy = {
    "@type": "MerchantReturnPolicy",
    applicableCountry: EU_EEA,
    returnPolicyCategory: "https://schema.org/MerchantReturnFiniteReturnWindow",
    merchantReturnDays: 14,
    returnMethod: "https://schema.org/ReturnByMail",
    returnFees: "https://schema.org/ReturnFeesCustomerResponsibleForShipping",
  };
  const jsonLd = [
    { "@context": "https://schema.org", "@type": "Product", name, image: images, description: p.short_descriptor || p.description,
      brand: { "@type": "Brand", name: "CaminoSapiens" },
      offers: { "@type": "Offer", price: Number(p.price), priceCurrency: p.currency || "EUR", availability: sold ? "https://schema.org/OutOfStock" : reserved ? "https://schema.org/PreOrder" : "https://schema.org/InStock", url: window.location.href, hasMerchantReturnPolicy: returnPolicy } },
    { "@context": "https://schema.org", "@type": "BreadcrumbList", itemListElement: [
      { "@type": "ListItem", position: 1, name: "Original Etchings", item: `${window.location.origin}/etchings` },
      { "@type": "ListItem", position: 2, name, item: window.location.href } ] },
  ];

  return (
    <div className="max-w-7xl mx-auto px-5 sm:px-8 py-14" data-testid="artwork-detail">
      <Seo title={p.seo?.title || `${name} — CaminoSapiens`} description={p.seo?.description || p.short_descriptor} image={p.primary_image} noindex={p.seo?.noindex} jsonLd={jsonLd} />
      <Breadcrumbs items={[{ label: "Home", to: "/" }, { label: "Original Etchings", to: "/etchings" }, { label: name }]} />

      <div className="grid lg:grid-cols-2 gap-12 lg:gap-16">
        <div>
          <motion.img key={activeImg} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.5 }}
            src={sized(images[activeImg], 1200)} srcSet={srcSet(images[activeImg], [600, 1000, 1600])} sizes="(min-width:1024px) 50vw, 100vw" alt={p.image_alt || p.title} className="w-full object-cover border border-[#E4E1DB]" data-testid="artwork-main-image" />
          {images.length > 1 && (
            <div className="flex gap-3 mt-4">
              {images.map((img, i) => (
                <button key={i} onClick={() => setActiveImg(i)} className={`w-20 h-20 border ${i === activeImg ? "border-[#B35D4B]" : "border-[#E4E1DB]"}`} data-testid={`artwork-thumb-${i}`}>
                  <img src={sized(img, 200)} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        <div>
          <p className="overline mb-3">{p.medium}</p>
          <h1 className="font-display text-4xl sm:text-5xl leading-tight" data-testid="artwork-title">{name}</h1>
          <div className="flex items-center gap-4 mt-5">
            <span className="text-2xl font-display" data-testid="artwork-price">{money(p.price, p.currency)}</span>
            {sold ? <span className="overline text-[#B35D4B]">Sold</span> :
             p.availability === "reserved" ? <span className="overline text-[#5B6C7A]">Reserved</span> :
             <span className="overline text-[#5B6C7A]">Available</span>}
          </div>
          {p.description && (
            <div className="text-[#4A4947] mt-6 leading-relaxed prose-camino" data-testid="artwork-description">
              {p.description.split("\n\n").map((para, i) => <p key={i}>{para}</p>)}
            </div>
          )}

          <div className="mt-8">
            <ShippingNotice className="mb-6" />
            {!unavailable ? (
              <div className="flex flex-wrap items-center gap-4">
                <div className="flex items-center border border-[#E4E1DB]" data-testid="artwork-qty">
                  <button onClick={() => setQty((q) => Math.max(1, q - 1))} disabled={qty <= 1} className="px-3 py-3 disabled:opacity-30" data-testid="artwork-qty-minus" aria-label="Decrease quantity">−</button>
                  <span className="px-4 py-3 border-x border-[#E4E1DB] text-sm min-w-[3rem] text-center" data-testid="artwork-qty-value">{qty}</span>
                  <button onClick={() => setQty((q) => Math.min(inv, q + 1))} disabled={qty >= inv} className="px-3 py-3 disabled:opacity-30" data-testid="artwork-qty-plus" aria-label="Increase quantity">+</button>
                </div>
                <button onClick={add} data-testid="artwork-add-to-cart" className="inline-flex items-center gap-2 bg-[#1C1B1A] text-[#F9F8F6] px-8 py-4 text-sm hover:bg-[#B35D4B] transition-colors">
                  <ShoppingBag className="w-4 h-4" /> Add to Cart
                </button>
                {inv <= 5 && <span className="text-xs text-[#4A4947]" data-testid="artwork-stock-note">{inv === 1 ? "Only 1 left" : `Only ${inv} left`}</span>}
              </div>
            ) : (
              <div data-testid="artwork-unavailable">
                <p className="text-sm text-[#4A4947]">
                  {sold ? "This original has found its home." : "This piece is currently reserved."}{" "}
                  <Link to="/custom-etching" className="link-underline text-[#B35D4B]">Commission your own etching →</Link>
                </p>
                {sold && (
                  <div className="mt-6 border-t border-[#E4E1DB] pt-6 max-w-sm">
                    {wlDone ? (
                      <p className="text-sm text-[#4A4947]" data-testid="waitlist-done">Thank you — we'll email you if a similar edition becomes available.</p>
                    ) : (
                      <>
                        <p className="overline mb-2">Join the waitlist</p>
                        <p className="text-sm text-[#4A4947] mb-3">Leave your email and we'll let you know if a similar edition appears.</p>
                        <form onSubmit={joinWaitlist} className="flex items-stretch border-b border-[#1C1B1A]">
                          <input type="email" required value={wlEmail} onChange={(e) => setWlEmail(e.target.value)}
                            placeholder="you@example.com" data-testid="waitlist-email"
                            className="flex-1 min-w-0 bg-transparent py-2 text-sm outline-none" />
                          <button type="submit" disabled={wlBusy} data-testid="waitlist-submit"
                            className="shrink-0 text-sm px-4 text-[#1C1B1A] hover:text-[#B35D4B] transition-colors disabled:opacity-40">
                            {wlBusy ? "…" : "Notify me"}
                          </button>
                        </form>
                      </>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="mt-10">
            <p className="overline mb-2">Details</p>
            <Spec label="Medium" value={p.medium} />
            <Spec label="Image dimensions" value={p.image_dimensions} />
            <Spec label="Paper dimensions" value={p.paper_dimensions} />
            <Spec label="Paper" value={p.paper_type} />
            <Spec label="Ink" value={p.ink} />
            <Spec label="Edition" value={p.edition_size} />
            <Spec label="Signed & numbered" value={p.signed_numbered} />
          </div>
        </div>
      </div>

      {related.length > 0 && (
        <div className="mt-24">
          <p className="overline mb-8">Related Stories</p>
          <div className="grid md:grid-cols-3 gap-8">
            {related.map((s) => (
              <Link key={s.slug} to={`/stories/${s.slug}`} className="group block" data-testid={`related-story-${s.slug}`}>
                <div className="overflow-hidden border border-[#E4E1DB]">
                  <img src={sized(s.hero_image, 700)} alt={s.hero_alt || s.title} className="w-full h-56 object-cover transition-transform duration-1000 group-hover:scale-105" loading="lazy" />
                </div>
                <h3 className="font-display text-xl mt-3">{s.title}</h3>
              </Link>
            ))}
          </div>
        </div>
      )}

      <div className="mt-20 text-center">
        <Link to="/etchings" className="inline-flex items-center gap-2 text-sm link-underline">View all etchings <ArrowRight className="w-4 h-4" /></Link>
      </div>
    </div>
  );
}
